import { throwAppError } from '@bangle.io/base-utils';
import { toast } from '@bangle.io/ui-components';
import { WsDirPath, WsPath } from '@bangle.io/ws-path';
import { FilePlus } from 'lucide-react';
import { c, getCtx } from '../helper';
import { validateInputPath, writeTextToClipboard } from '../utils';

export const noteManagementHandlers = [
  c(
    'command::ui:create-note-dialog',
    ({ workbenchState }, { prefillName }, key) => {
      const { store, dispatch } = getCtx(key);
      store.set(workbenchState.$singleInputDialog, () => {
        return {
          dialogId: 'dialog::new-note-dialog',
          title: t.app.dialogs.createNote.title,
          description: t.app.dialogs.createNote.description,
          inputLabel: t.app.dialogs.createNote.inputLabel,
          placeholder: t.app.dialogs.createNote.placeholder,
          submitText: t.app.dialogs.createNote.submitText,
          onSelect: (input) => {
            dispatch('command::ws:new-note-from-input', {
              inputPath: input.trim(),
              promptRename: false,
            });
            dispatch('command::ui:focus-editor', null);
          },
          initialSearch: prefillName,
        };
      });
    },
  ),

  c(
    'command::ui:recover-note',
    ({ navigation, workspaceState }, { wsPath }, key) => {
      const { store } = getCtx(key);
      const targetWsPath = wsPath || store.get(workspaceState.$currentWsPath);

      if (!targetWsPath) {
        throwAppError(
          'error::workspace:not-opened',
          t.app.errors.workspace.notOpened,
          { wsPath },
        );
      }

      const filePath = WsPath.safeParse(targetWsPath).data?.asFile();

      if (!filePath) {
        throwAppError(
          'error::file:invalid-note-path',
          t.app.errors.file.invalidNotePath,
          {
            invalidWsPath: targetWsPath.toString(),
          },
        );
      }

      // Prefill the Recover page's search with the full wsPath so the table
      // shows exactly this note's snapshots.
      navigation.goRecoverySettings({ search: filePath.wsPath });
    },
  ),

  c(
    'command::ui:delete-note-dialog',
    ({ workbenchState, workspaceState }, { wsPath }, key) => {
      const { store, dispatch } = getCtx(key);
      const targetWsPath = wsPath || store.get(workspaceState.$currentWsPath);

      if (!targetWsPath) {
        throwAppError(
          'error::workspace:not-opened',
          t.app.errors.workspace.notOpened,
          { wsPath },
        );
      }

      const filePath = WsPath.safeParse(targetWsPath).data?.asFile();

      if (!filePath) {
        throwAppError(
          'error::file:invalid-note-path',
          t.app.errors.file.invalidNotePath,
          {
            invalidWsPath: targetWsPath.toString(),
          },
        );
      }

      store.set(workbenchState.$alertDialog, () => {
        return {
          dialogId: 'dialog::alert',
          title: t.app.dialogs.confirmDelete.title,
          tone: 'destructive',
          description: t.app.dialogs.confirmDelete.description({
            fileName: filePath.fileNameWithoutExtension,
          }),
          continueText: t.app.dialogs.confirmDelete.continueText,
          onContinue: () => {
            dispatch('command::ws:delete-ws-path', {
              wsPath: filePath.wsPath,
            });
            dispatch('command::ui:focus-editor', null);
          },
          onCancel: () => {},
        };
      });
    },
  ),

  c('command::ui:delete-file-dialog', ({ workbenchState }, { wsPath }, key) => {
    const { store, dispatch } = getCtx(key);
    const filePath = WsPath.safeParse(wsPath).data?.asFile();

    if (!filePath) {
      throwAppError(
        'error::file:invalid-note-path',
        t.app.errors.file.invalidNotePath,
        {
          invalidWsPath: wsPath,
        },
      );
    }

    store.set(workbenchState.$alertDialog, () => {
      return {
        dialogId: 'dialog::delete-file-alert',
        title: t.app.dialogs.confirmDelete.title,
        tone: 'destructive',
        description: t.app.dialogs.confirmDelete.description({
          fileName: filePath.fileName,
        }),
        continueText: t.app.dialogs.confirmDelete.continueText,
        onContinue: () => {
          dispatch('command::ws:delete-ws-path', {
            wsPath: filePath.wsPath,
          });
          dispatch('command::ui:focus-editor', null);
        },
        onCancel: () => {},
      };
    });
  }),

  c(
    'command::ui:delete-files-dialog',
    ({ workbenchState }, { wsPaths }, key) => {
      const { store, dispatch } = getCtx(key);
      const filePaths = [...new Set(wsPaths)].map(
        (selectedWsPath) =>
          WsPath.safeParse(selectedWsPath).data?.asFile() ?? null,
      );

      if (filePaths.length === 0 || filePaths.some((path) => path === null)) {
        throwAppError(
          'error::file:invalid-note-path',
          t.app.errors.file.invalidNotePath,
          {
            invalidWsPath: wsPaths.join(', '),
          },
        );
      }

      const validFilePaths = filePaths.filter(
        (path): path is NonNullable<(typeof filePaths)[number]> =>
          path !== null,
      );

      store.set(workbenchState.$alertDialog, () => {
        return {
          dialogId: 'dialog::delete-files-alert',
          title: t.app.dialogs.confirmDeleteFiles.title,
          tone: 'destructive',
          description: t.app.dialogs.confirmDeleteFiles.description({
            count: validFilePaths.length,
            fileNames: validFilePaths.map((path) => path.fileName),
          }),
          continueText: t.app.dialogs.confirmDeleteFiles.continueText,
          onContinue: () => {
            dispatch('command::ws:delete-ws-paths', {
              wsPaths: validFilePaths.map((path) => path.wsPath),
            });
            dispatch('command::ui:focus-editor', null);
          },
          onCancel: () => {},
        };
      });
    },
  ),

  c(
    'command::ui:rename-note-dialog',
    ({ workspaceState, workbenchState }, { wsPath }, key) => {
      const { store, dispatch } = getCtx(key);
      const { data: oldWsPath } = WsPath.safeParse(
        wsPath || store.get(workspaceState.$currentWsPath) || '',
      );

      if (!oldWsPath) {
        throwAppError(
          'error::workspace:not-opened',
          t.app.errors.workspace.notOpened,
          {
            wsPath,
          },
        );
      }
      const fileOldWsPath = oldWsPath.asFile();

      if (!fileOldWsPath) {
        throwAppError(
          'error::file:invalid-note-path',
          t.app.errors.file.invalidNotePath,
          {
            invalidWsPath: oldWsPath.wsPath,
          },
        );
      }

      store.set(workbenchState.$singleInputDialog, () => {
        const stem = fileOldWsPath.fileNameWithoutExtension;
        const isNewUntitled = /^untitled-\d+$/i.test(stem);
        return {
          dialogId: 'dialog::rename-note-dialog',
          title: isNewUntitled
            ? t.app.dialogs.renameNote.nameTitle
            : t.app.dialogs.renameNote.title,
          description: isNewUntitled
            ? t.app.dialogs.renameNote.nameDescription
            : t.app.dialogs.renameNote.description({
                fileNameWithoutExtension: stem,
              }),
          placeholder: t.app.dialogs.renameNote.placeholder,
          initialSearch: isNewUntitled ? '' : stem,
          allowEmptySubmit: isNewUntitled,
          submitText: isNewUntitled
            ? t.app.dialogs.renameNote.nameSubmitText
            : t.app.dialogs.renameNote.submitText,
          onDismiss: isNewUntitled
            ? () => {
                dispatch('command::ui:focus-editor', null);
              }
            : undefined,
          onSelect: (input) => {
            const trimmedInput = input.trim();
            if (!trimmedInput) {
              if (isNewUntitled) {
                dispatch('command::ui:focus-editor', null);
                return;
              }
              throwAppError(
                'error::file:invalid-operation',
                t.app.errors.file.invalidNoteName,
                {
                  oldWsPath: oldWsPath.wsPath,
                  newWsPath: input,
                  operation: 'rename',
                },
              );
            }

            if (trimmedInput === stem) {
              dispatch('command::ui:focus-editor', null);
              return;
            }

            const newName =
              trimmedInput +
              (trimmedInput.endsWith(WsPath.DEFAULT_NOTE_EXTENSION)
                ? ''
                : WsPath.DEFAULT_NOTE_EXTENSION);

            const parentDirPath = fileOldWsPath.getParent();

            // Construct the relative path for validation
            const relativeNewPath = parentDirPath
              ? WsPath.pathJoin(parentDirPath.path, newName)
              : newName;

            // Validate the constructed relative path
            validateInputPath(relativeNewPath);

            if (!parentDirPath) {
              // This case should ideally be handled by validateInputPath if newName contains '/',
              // but we keep the check for clarity and robustness.
              // If parentDirPath is null, it means the original file was at the root.
              // We construct the new path directly from the newName.
              if (newName.includes('/')) {
                // Prevent creating subdirectories during rename implicitly
                throwAppError(
                  'error::file:invalid-operation',
                  t.app.errors.file.cannotMoveDuringRename,
                  {
                    oldWsPath: oldWsPath.wsPath,
                    newWsPath: input,
                    operation: 'rename',
                  },
                );
              }
            }

            const newWsPath = fileOldWsPath.replaceFileName(newName);

            dispatch('command::ws:rename-ws-path', {
              newWsPath: newWsPath.wsPath,
              wsPath: oldWsPath.wsPath,
            });
            dispatch('command::ui:focus-editor', null);
          },
        };
      });
    },
  ),

  c('command::ui:rename-file-dialog', ({ workbenchState }, { wsPath }, key) => {
    const { store, dispatch } = getCtx(key);
    const filePath = WsPath.safeParse(wsPath).data?.asFile();

    if (!filePath) {
      throwAppError(
        'error::file:invalid-note-path',
        t.app.errors.file.invalidNotePath,
        {
          invalidWsPath: wsPath,
        },
      );
    }

    store.set(workbenchState.$singleInputDialog, () => {
      return {
        dialogId: 'dialog::rename-file-dialog',
        title: t.app.dialogs.renameFile.title,
        description: t.app.dialogs.renameFile.description({
          fileName: filePath.fileName,
        }),
        inputLabel: t.app.dialogs.renameFile.inputLabel,
        placeholder: t.app.dialogs.renameFile.placeholder,
        initialSearch: filePath.fileName,
        submitText: t.app.dialogs.renameFile.submitText,
        onSelect: (input) => {
          const newFileName = input.trim();
          if (!newFileName) {
            throwAppError(
              'error::file:invalid-operation',
              t.app.errors.file.invalidNoteName,
              {
                oldWsPath: filePath.wsPath,
                newWsPath: input,
                operation: 'rename-file',
              },
            );
          }

          if (newFileName.includes('/')) {
            throwAppError(
              'error::file:invalid-operation',
              t.app.errors.file.cannotMoveDuringRename,
              {
                oldWsPath: filePath.wsPath,
                newWsPath: input,
                operation: 'rename-file',
              },
            );
          }

          const parentDirPath = filePath.getParent();
          const relativeNewPath = parentDirPath
            ? WsPath.pathJoin(parentDirPath.path, newFileName)
            : newFileName;
          validateInputPath(relativeNewPath);

          dispatch('command::ws:rename-ws-path', {
            newWsPath: filePath.replaceFileName(newFileName).wsPath,
            wsPath: filePath.wsPath,
          });
          dispatch('command::ui:focus-editor', null);
        },
      };
    });
  }),

  c('command::ui:copy-workspace-path', async (_, { wsPath }) => {
    const target = WsPath.safeParseFile(wsPath).data;
    const copiedPath = target?.path;

    if (!copiedPath) {
      toast.error(t.app.toasts.pathCopyFailed);
      return;
    }

    try {
      await writeTextToClipboard(copiedPath);
      toast.success(t.app.toasts.pathCopied);
    } catch {
      toast.error(t.app.toasts.pathCopyFailed);
    }
  }),

  c(
    'command::ui:move-note-dialog',
    async (
      { workspaceState, workbenchState, workspaceOps, fileSystem },
      { wsPath },
      key,
    ) => {
      const { store, dispatch } = getCtx(key);
      const { data: oldWsPath } = WsPath.safeParse(
        wsPath || store.get(workspaceState.$currentWsPath) || '',
      );

      if (!oldWsPath) {
        throwAppError(
          'error::workspace:not-opened',
          t.app.errors.workspace.notOpened,
          {
            wsPath,
          },
        );
      }

      const filePath = WsPath.assertFile(oldWsPath.wsPath);
      const oldDirWsPath =
        filePath.getParent()?.wsPath ?? `${filePath.wsName}:`;

      const workspaces = await workspaceOps.getAllWorkspaces();
      const options: { id: string; title: string }[] = [];
      for (const workspace of workspaces) {
        let wsFiles: string[] = [];
        try {
          wsFiles = await fileSystem.listWorkspaceFiles(workspace.name);
        } catch {
          continue;
        }
        const dirPaths = [
          ...new Set(
            wsFiles.map((path) => {
              const parsed = WsPath.fromString(path);
              return parsed.getParent()?.path ?? '';
            }),
          ),
        ].filter((dirPath) => dirPath !== '');
        const rootId = `${workspace.name}:`;
        if (rootId !== oldDirWsPath) {
          options.push({ id: rootId, title: `${workspace.name} /` });
        }
        for (const dirPath of dirPaths) {
          const dirId = WsDirPath.fromParts(workspace.name, dirPath).wsPath;
          if (dirId === oldDirWsPath) {
            continue;
          }
          options.push({ id: dirId, title: `${workspace.name} / ${dirPath}` });
        }
      }

      const ROOT_FALLBACK = '<{bangle_root}>';
      void ROOT_FALLBACK;

      store.set(workbenchState.$singleSelectDialog, () => {
        return {
          dialogId: 'dialog::move-note-dialog',
          title: t.app.dialogs.moveNote.title({
            fileNameWithoutExtension: filePath.fileNameWithoutExtension,
          }),
          searchPlaceholder: t.app.dialogs.moveNote.searchPlaceholder,
          emptyMessage: t.app.dialogs.moveNote.emptyMessage,
          emptyActionText: t.app.dialogs.moveNote.emptyActionText,
          options,
          Icon: FilePlus,
          groupLabel: t.app.dialogs.moveNote.groupLabel,
          hints:
            options.length > 0
              ? [
                  t.app.dialogs.moveNote.hintClick,
                  t.app.dialogs.moveNote.hintDrag,
                ]
              : [t.app.dialogs.moveNote.hintCreateDirectory],
          onEmptyAction: () => {
            dispatch('command::ui:create-directory-dialog', {
              pathPrefix: undefined,
            });
          },
          onSelect: (selectedDir) => {
            dispatch('command::ws:move-ws-path', {
              wsPath: oldWsPath.wsPath,
              destDirWsPath: selectedDir.id,
            });
            dispatch('command::ui:focus-editor', null);
          },
        };
      });
    },
  ),

  c(
    'command::ui:move-notes-dialog',
    async (
      { workspaceState, workbenchState, workspaceOps, fileSystem },
      { wsPaths, dirWsPath },
      key,
    ) => {
      const { store, dispatch } = getCtx(key);
      let sources: string[] = [...new Set(wsPaths ?? [])];
      if (dirWsPath) {
        const dirPath = WsPath.fromString(dirWsPath).asDir();
        if (!dirPath) {
          throwAppError(
            'error::ws-path:invalid-ws-path',
            t.app.errors.wsPath.invalidDirectoryPath,
            { invalidPath: dirWsPath },
          );
        }
        sources = store
          .get(workspaceState.$wsPaths)
          .map((path) => path.wsPath)
          .filter((candidate) => {
            const parsed = WsPath.fromString(candidate);
            return (
              parsed.wsName === dirPath.wsName &&
              parsed.path.startsWith(dirPath.path) &&
              candidate !== dirWsPath
            );
          });
      }
      const files = sources.filter(
        (candidate) => WsPath.safeParseFile(candidate).ok,
      );
      if (files.length === 0) {
        throwAppError(
          'error::file:invalid-note-path',
          t.app.errors.file.invalidNotePath,
          { invalidWsPath: (wsPaths ?? []).join(', ') || dirWsPath || '' },
        );
      }

      const workspaces = await workspaceOps.getAllWorkspaces();
      const options: { id: string; title: string }[] = [];
      for (const workspace of workspaces) {
        let wsFiles: string[] = [];
        try {
          wsFiles = await fileSystem.listWorkspaceFiles(workspace.name);
        } catch {
          continue;
        }
        const dirPaths = [
          ...new Set(
            wsFiles.map((path) => {
              const parsed = WsPath.fromString(path);
              return parsed.getParent()?.path ?? '';
            }),
          ),
        ].filter((dirPath) => dirPath !== '');
        options.push({
          id: `${workspace.name}:`,
          title: `${workspace.name} /`,
        });
        for (const dirPath of dirPaths) {
          const dirId = WsDirPath.fromParts(workspace.name, dirPath).wsPath;
          options.push({ id: dirId, title: `${workspace.name} / ${dirPath}` });
        }
      }

      store.set(workbenchState.$singleSelectDialog, () => {
        return {
          dialogId: 'dialog::move-notes-dialog',
          title: t.app.dialogs.moveNotes.title({ count: files.length }),
          searchPlaceholder: t.app.dialogs.moveNotes.searchPlaceholder,
          emptyMessage: t.app.dialogs.moveNotes.emptyMessage,
          options,
          Icon: FilePlus,
          groupLabel: t.app.dialogs.moveNotes.groupLabel,
          onSelect: (selectedDir) => {
            if (files.length === 1 && files[0]) {
              dispatch('command::ws:move-ws-path', {
                wsPath: files[0],
                destDirWsPath: selectedDir.id,
              });
            } else {
              dispatch('command::ws:move-ws-paths', {
                wsPaths: files,
                destDirWsPath: selectedDir.id,
              });
            }
            dispatch('command::ui:focus-editor', null);
          },
        };
      });
    },
  ),

  c(
    'command::ui:create-directory-dialog',
    ({ workbenchState, workspaceState }, { pathPrefix }, key) => {
      const { store, dispatch } = getCtx(key);
      const wsName = store.get(workspaceState.$currentWsName);

      if (!wsName) {
        throwAppError(
          'error::workspace:not-opened',
          t.app.errors.workspace.notOpened,
          {},
        );
      }

      store.set(workbenchState.$singleInputDialog, () => {
        return {
          dialogId: 'dialog::new-directory-dialog',
          title: t.app.dialogs.createDirectory.title,
          description: t.app.dialogs.createDirectory.description,
          placeholder: t.app.dialogs.createDirectory.placeholder,
          submitText: t.app.dialogs.createDirectory.submitText,
          onSelect: (_input) => {
            const input = _input.trim();
            const dirPath = pathPrefix
              ? WsPath.pathJoin(pathPrefix, input)
              : input;

            validateInputPath(dirPath);
            dispatch('command::ws:create-directory', {
              dirWsPath: WsDirPath.fromParts(wsName, dirPath).wsPath,
            });
          },
        };
      });
    },
  ),

  c(
    'command::ui:rename-directory-dialog',
    ({ workbenchState }, { dirWsPath }, key) => {
      const { store, dispatch } = getCtx(key);
      const dirPath = WsPath.fromString(dirWsPath).asDir();

      if (!dirPath || dirPath.isRoot) {
        throwAppError(
          'error::ws-path:invalid-ws-path',
          t.app.errors.wsPath.invalidDirectoryPath,
          {
            invalidPath: dirWsPath,
          },
        );
      }

      store.set(workbenchState.$singleInputDialog, () => {
        return {
          dialogId: 'dialog::rename-directory-dialog',
          title: t.app.dialogs.renameDirectory.badgeText({
            directoryName: dirPath.name,
          }),
          inputLabel: t.app.dialogs.renameDirectory.placeholder,
          placeholder: t.app.dialogs.renameDirectory.placeholder,
          submitText: t.app.dialogs.renameDirectory.optionTitle,
          initialSearch: dirPath.name,
          Icon: FilePlus,
          onSelect: (input) => {
            const newDirectoryName = input.trim();
            if (!newDirectoryName) {
              throwAppError(
                'error::file:invalid-operation',
                t.app.errors.file.invalidNoteName,
                {
                  oldWsPath: dirWsPath,
                  newWsPath: input,
                  operation: 'rename-directory',
                },
              );
            }

            if (newDirectoryName.includes('/')) {
              throwAppError(
                'error::file:invalid-operation',
                t.app.errors.file.cannotMoveDuringRename,
                {
                  oldWsPath: dirWsPath,
                  newWsPath: input,
                  operation: 'rename-directory',
                },
              );
            }

            validateInputPath(newDirectoryName);

            const parentPath = dirPath.getParent()?.path ?? '';
            const newDirPath = WsPath.pathJoin(parentPath, newDirectoryName);

            dispatch('command::ws:rename-directory', {
              oldDirWsPath: dirPath.wsPath,
              newDirWsPath: WsDirPath.fromParts(dirPath.wsName, newDirPath)
                .wsPath,
            });
            dispatch('command::ui:focus-editor', null);
          },
        };
      });
    },
  ),

  c(
    'command::ui:delete-directory-dialog',
    ({ workbenchState }, { dirWsPath }, key) => {
      const { store, dispatch } = getCtx(key);
      const dirPath = WsPath.fromString(dirWsPath).asDir();

      if (!dirPath || dirPath.isRoot) {
        throwAppError(
          'error::ws-path:invalid-ws-path',
          t.app.errors.wsPath.invalidDirectoryPath,
          {
            invalidPath: dirWsPath,
          },
        );
      }

      store.set(workbenchState.$alertDialog, () => {
        return {
          dialogId: 'dialog::delete-directory-alert',
          title: t.app.dialogs.confirmDeleteDirectory.title,
          tone: 'destructive',
          description: t.app.dialogs.confirmDeleteDirectory.description({
            directoryName: dirPath.name,
          }),
          continueText: t.app.dialogs.confirmDeleteDirectory.continueText,
          onContinue: () => {
            dispatch('command::ws:delete-directory', {
              dirWsPath: dirPath.wsPath,
            });
            dispatch('command::ui:focus-editor', null);
          },
          onCancel: () => {},
        };
      });
    },
  ),
];
